export type ConnectionStatus = 'online' | 'offline' | 'reconnecting' | 'error';

export interface ConnectionEvent {
    status: ConnectionStatus;
    timestamp: Date;
    error?: string;
}

// Browser-compatible EventEmitter implementation
class SimpleEventEmitter {
    private events: { [key: string]: ((...args: unknown[]) => void)[] } = {};

    on(event: string, listener: (...args: unknown[]) => void) {
        if (!this.events[event]) {
            this.events[event] = [];
        }
        this.events[event].push(listener);
    }

    off(event: string, listener: (...args: unknown[]) => void) {
        if (!this.events[event]) return;
        this.events[event] = this.events[event].filter(l => l !== listener);
    }

    emit(event: string, ...args: unknown[]) {
        if (!this.events[event]) return;
        this.events[event].forEach(listener => listener(...args));
    }

    removeAllListeners() {
        this.events = {};
    }
}

class ConnectionService extends SimpleEventEmitter {
    private status: ConnectionStatus = 'online';
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectDelay = 1000; // Start with 1 second
    private maxReconnectDelay = 30000; // Max 30 seconds
    private reconnectTimer?: number;
    private pingInterval?: number;
    private lastPingTime = 0;
    private boundHandleOnline: () => void;
    private boundHandleOffline: () => void;

    constructor() {
        super();
        this.boundHandleOnline = this.handleOnline.bind(this);
        this.boundHandleOffline = this.handleOffline.bind(this);
        this.initializeConnectionMonitoring();
    }

    private initializeConnectionMonitoring() {
        // Monitor online/offline events
        window.addEventListener('online', this.boundHandleOnline);
        window.addEventListener('offline', this.boundHandleOffline);

        // Start periodic connectivity checks
        this.startPingMonitoring();

        // Initial status
        this.updateStatus(navigator.onLine ? 'online' : 'offline');
    }

    private handleOnline() {
        logger.debug('🌐 Network connection restored');
        this.reconnectAttempts = 0;
        this.updateStatus('online');
        this.clearReconnectTimer();
    }

    private handleOffline() {
        logger.debug('🚫 Network connection lost');
        this.updateStatus('offline');
        this.startReconnectProcess();
    }

    private startPingMonitoring() {
        this.pingInterval = window.setInterval(async () => {
            if (this.status === 'online') {
                const isConnected = await this.checkConnectivity();
                if (!isConnected && navigator.onLine) {
                    // Browser thinks we're online but API is unreachable
                    this.updateStatus('error', 'API server unreachable');
                    this.startReconnectProcess();
                }
            }
        }, 30000); // Check every 30 seconds
    }

    private async checkConnectivity(): Promise<boolean> {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);

            const response = await fetch('/api/health', {
                method: 'HEAD',
                signal: controller.signal,
                cache: 'no-cache'
            });

            clearTimeout(timeoutId);
            this.lastPingTime = Date.now();
            return response.ok;
        } catch (error) {
            logger.warn('Connectivity check failed:', error);
            return false;
        }
    }

    private startReconnectProcess() {
        if (this.reconnectTimer || this.status === 'reconnecting') {
            return;
        }

        this.updateStatus('reconnecting');
        this.scheduleReconnect();
    }

    private scheduleReconnect() {
        const delay = Math.min(
            this.reconnectDelay * Math.pow(2, this.reconnectAttempts),
            this.maxReconnectDelay
        );

        logger.debug(`🔄 Scheduling reconnect attempt ${this.reconnectAttempts + 1} in ${delay}ms`);

        this.reconnectTimer = window.setTimeout(async () => {
            this.reconnectTimer = undefined;
            await this.attemptReconnect();
        }, delay);
    }

    private async attemptReconnect() {
        this.reconnectAttempts++;

        logger.debug(`🔄 Reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);

        const isConnected = await this.checkConnectivity();

        if (isConnected) {
            logger.debug('✅ Reconnection successful');
            this.reconnectAttempts = 0;
            this.updateStatus('online');
            this.emit('reconnected');
        } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            logger.error('❌ Max reconnection attempts reached');
            this.updateStatus('error', 'Max reconnection attempts exceeded');
            this.emit('reconnectFailed');
        } else {
            // Schedule next attempt
            this.scheduleReconnect();
        }
    }

    private clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = undefined;
        }
    }

    private updateStatus(status: ConnectionStatus, error?: string) {
        const previousStatus = this.status;
        this.status = status;

        const event: ConnectionEvent = {
            status,
            timestamp: new Date(),
            error
        };

        if (import.meta.env.DEV) {
            logger.debug(`🔗 Connection status: ${previousStatus} → ${status}`, error ? `(${error})` : '');
        }

        this.emit('statusChange', event);
        this.emit(status, event);
    }

    // Public API
    public getStatus(): ConnectionStatus {
        return this.status;
    }

    public isOnline(): boolean {
        return this.status === 'online';
    }

    public isReconnecting(): boolean {
        return this.status === 'reconnecting';
    }

    public getLastPingTime(): number {
        return this.lastPingTime;
    }

    public forceReconnect(): void {
        this.reconnectAttempts = 0;
        this.clearReconnectTimer();
        this.startReconnectProcess();
    }

    public destroy(): void {
        window.removeEventListener('online', this.boundHandleOnline);
        window.removeEventListener('offline', this.boundHandleOffline);

        if (this.pingInterval) {
            clearInterval(this.pingInterval);
        }

        this.clearReconnectTimer();
        this.removeAllListeners();
    }
}

// Singleton instance
export const connectionService = new ConnectionService();

// React hook for connection status
import React from 'react';
import { logger } from '../utils/logger';

export const useConnectionStatus = () => {
    const [status, setStatus] = React.useState<ConnectionStatus>(connectionService.getStatus());
    const [lastPing, setLastPing] = React.useState<number>(connectionService.getLastPingTime());

    React.useEffect(() => {
        const handleStatusChange = (...args: unknown[]) => {
            const event = args[0] as ConnectionEvent;
            setStatus(event.status);
        };

        const handlePingUpdate = () => {
            setLastPing(connectionService.getLastPingTime());
        };

        connectionService.on('statusChange', handleStatusChange);
        connectionService.on('online', handlePingUpdate);

        return () => {
            connectionService.off('statusChange', handleStatusChange);
            connectionService.off('online', handlePingUpdate);
        };
    }, []);

    return {
        status,
        isOnline: status === 'online',
        isReconnecting: status === 'reconnecting',
        lastPing: lastPing > 0 ? new Date(lastPing) : null,
        forceReconnect: () => connectionService.forceReconnect()
    };
};
